import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { startOfDay, isBefore } from 'date-fns';
import { createOffer } from '../api';
import { OFFER_ERROR_MESSAGES } from '../errors';
import {
  OfferFormData,
  OfferFormErrors,
  INITIAL_FORM_DATA,
} from '../types/offerFormTypes';

// ============================================================================
// Hook
// ============================================================================

export function useCreateOffer() {
  const navigate = useNavigate();

  // Form state
  const [formData, setFormData] = useState<OfferFormData>(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState<OfferFormErrors>({});
  const [selectedPlans, setSelectedPlans] = useState<string[]>([]);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [snackbarError, setSnackbarError] = useState('');
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      // Basic validation
      const newErrors: OfferFormErrors = {};

      // Offer Name
      if (!formData.offerName.trim()) {
        newErrors.offerName = OFFER_ERROR_MESSAGES.OFFER_NAME_REQUIRED;
      } else if (formData.offerName.length > 200) {
        newErrors.offerName = OFFER_ERROR_MESSAGES.OFFER_NAME_MAX_LENGTH;
      }

      // Discount Value
      const discValueStr = formData.discountValue.replace(',', '.').trim();
      if (!discValueStr) {
        newErrors.discountValue = OFFER_ERROR_MESSAGES.OFFER_NAME_REQUIRED; // Reuse mandatory msg
      } else {
        const discValue = parseFloat(discValueStr);
        if (Number.isNaN(discValue)) {
          newErrors.discountValue = 'Invalid number';
        } else if (discValue < 0) {
          newErrors.discountValue = OFFER_ERROR_MESSAGES.DISCOUNT_VALUE_MIN;
        } else if (discValue > 100) {
          newErrors.discountValue = OFFER_ERROR_MESSAGES.DISCOUNT_VALUE_MAX;
        } else {
          const decimals = discValueStr.split('.')[1];
          if (decimals && decimals.length > 2) {
            newErrors.discountValue =
              OFFER_ERROR_MESSAGES.DISCOUNT_VALUE_DECIMAL;
          }
        }
      }

      // Validity Period (Dates)
      const today = startOfDay(new Date());

      if (!startDate) {
        newErrors.startDate = OFFER_ERROR_MESSAGES.DATE_REQUIRED;
      } else if (isBefore(startDate, today)) {
        newErrors.startDate = OFFER_ERROR_MESSAGES.START_DATE_PAST;
      }

      if (!endDate) {
        newErrors.endDate = OFFER_ERROR_MESSAGES.INVALID_END_DATE_FORMAT;
      } else if (isBefore(endDate, today)) {
        newErrors.endDate = OFFER_ERROR_MESSAGES.END_DATE_PAST;
      } else if (startDate && isBefore(endDate, startDate)) {
        newErrors.endDate = OFFER_ERROR_MESSAGES.END_DATE_BEFORE_START;
      }

      setErrors(newErrors);

      if (Object.keys(newErrors).length === 0) {
        setIsSubmitting(true);
        try {
          const result = await createOffer({
            offerName: formData.offerName,
            subscriptionPlanIds: selectedPlans,
            discountType: formData.offerType,
            discountValue: formData.discountValue,
            startDate: startDate as Date,
            endDate: endDate as Date,
          });

          if (result.success) {
            // Success logic - navigate to offers list
            navigate('/Oferty', {
              state: { successMessage: 'Oferta została utworzona.' },
            });
          } else {
            const errorMsg = result.error || OFFER_ERROR_MESSAGES.DEFAULT_ERROR;

            // Check if error is related to existing offer name
            if (
              errorMsg.toLowerCase().includes('already exists') &&
              errorMsg.toLowerCase().includes('name')
            ) {
              setErrors((prev) => ({
                ...prev,
                offerName: OFFER_ERROR_MESSAGES.OFFER_NAME_EXISTS,
              }));
            } else {
              setSnackbarError(errorMsg);
            }

            if (result.fieldErrors) {
              setErrors((prev) => ({ ...prev, ...result.fieldErrors }));
            }
          }
        } catch (_error) {
          setSnackbarError(OFFER_ERROR_MESSAGES.UNEXPECTED_ERROR);
          setIsSnackbarOpen(true);
        } finally {
          setIsSubmitting(false);
        }
      }
    },
    [formData, selectedPlans, startDate, endDate, navigate]
  );

  // True only if the user has provided any input that distinguishes the form from its initial state.
  // offerType defaults to 'Procentowy' so we compare against that, and selectedPlans defaults to [].
  const hasContent = Boolean(
    formData.offerName ||
    formData.discountValue ||
    selectedPlans.length > 0 ||
    startDate !== undefined ||
    endDate !== undefined
  );

  const handleCancel = useCallback(() => {
    if (!hasContent) {
      navigate(-1);
      return;
    }
    setIsCancelModalOpen(true);
  }, [hasContent, navigate]);

  const handleCloseCancelModal = useCallback(() => {
    setIsCancelModalOpen(false);
  }, []);

  const handleConfirmCancel = useCallback(() => {
    setIsCancelModalOpen(false);
    navigate(-1); // Go back or to a specific route
  }, [navigate]);

  const handleCloseSnackbar = useCallback(() => {
    setIsSnackbarOpen(false);
  }, []);

  return {
    // Form state
    formData,
    setFormData,
    errors,
    setErrors,
    selectedPlans,
    setSelectedPlans,
    startDate,
    setStartDate,
    endDate,
    setEndDate,

    // UI state
    isSubmitting,
    isSnackbarOpen,
    snackbarError,
    isCancelModalOpen,

    // Handlers
    handleSubmit,
    handleCancel,
    handleCloseCancelModal,
    handleConfirmCancel,
    handleCloseSnackbar,
  };
}
